import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { errName, friendlyAuthMessage } from '../auth/cognitoError';
import { useAuth } from '../auth/useAuth';
import { AuthScreen, Button, ErrorText, Honeypot, TextField } from '../components/ui';

export function SignUp() {
  const { signUp, signIn } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [password, setPassword] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (honeypot) return; // bot
    setError(null);
    // No verification email to catch a typo, so confirm the address matches.
    if (email.trim().toLowerCase() !== confirmEmail.trim().toLowerCase()) {
      setError("Those emails don't match — please re-check.");
      return;
    }
    setBusy(true);
    try {
      await signUp(email.trim(), password, name.trim());
      // The PreSignUp trigger auto-confirms the account — no verification step, so log
      // the guest straight in and drop them on the app.
      await signIn(email.trim(), password);
      navigate('/overview', { replace: true });
    } catch (err) {
      if (errName(err) === 'UsernameExistsException') {
        setError('An account with this email already exists — please sign in below.');
        return;
      }
      setError(friendlyAuthMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthScreen>
      <h1 className="serif" style={{ fontSize: '2.2rem', fontWeight: 500, margin: '0 0 4px' }}>
        Create an account
      </h1>
      <p style={{ color: 'var(--text-dim)', fontSize: 14, margin: '0 0 22px' }}>
        So we can save your RSVP.
      </p>
      <form onSubmit={onSubmit}>
        <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <TextField
          label="Confirm email"
          type="email"
          autoComplete="email"
          value={confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          onPaste={(e) => e.preventDefault()}
          required
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <Honeypot value={honeypot} onChange={setHoneypot} />
        <ErrorText>{error}</ErrorText>
        <Button type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </Button>
      </form>
      <p style={{ textAlign: 'center', color: 'var(--text-dim)', fontSize: 14, marginTop: 16 }}>
        Already have one? <Link to="/login">Sign in</Link>
      </p>
    </AuthScreen>
  );
}
